/**
 * Area Query Hooks
 *
 * React Query hooks for managing area data state with server-side rendering support.
 * Uses tag-based cache invalidation for automatic updates when related data changes.
 */

import { useMutation, useQuery, useQueryClient } from '@zg-rentals/react-query5';
import type { UseQueryOptions } from '@zg-rentals/react-query5';
import { area } from '@/src/lib/api/endpoints/area';
import type { AreaResponse } from '@/src/lib/api';

// Query keys for type-safe cache management
export const areaKeys = {
  all: ['area'] as const,
  byIp: (ip: string) => [...areaKeys.all, 'byIp', ip] as const,
  byId: (resourceId: string) => [...areaKeys.all, 'byId', resourceId] as const,
  byResourceId: (resourceId: string) => areaKeys.byId(resourceId),
};

/**
 * Hook for fetching area data by IP address
 *
 * Used on the homepage to determine user's location and show relevant listings.
 * Can be hydrated with SSR data from App Router server components.
 *
 * @param ip - User's IP address
 * @param options - React Query options, including initialData from SSR
 * @returns Query result with area data, loading state, and error state
 *
 * @example
 * ```tsx
 * // Client-side usage
 * const { data: area, isLoading } = useAreaByIp(userIp);
 *
 * // With SSR hydration
 * const { data: area } = useAreaByIp(userIp, { initialData: ssrAreaData });
 * ```
 */
export const useAreaByIp = (
  ip: string,
  options?: Omit<UseQueryOptions<AreaResponse | null>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: areaKeys.byIp(ip),
    queryFn: () => area.byIp(ip),
    staleTime: 5 * 60 * 1000, // 5 minutes - area data doesn't change often
    meta: {
      tags: ['area'], // Tag for cache invalidation
    },
    ...options,
  });
};

/**
 * Hook for fetching area data by resource ID
 *
 * Used when user manually selects a location or navigates to a specific area.
 *
 * @param resourceId - Area resource ID (e.g., "san-francisco-ca")
 * @param options - React Query options
 * @returns Query result with area data
 *
 * @example
 * ```tsx
 * const { data: area } = useAreaByResourceId('san-francisco-ca');
 * ```
 */
export const useAreaByResourceId = (
  resourceId: string,
  options?: Omit<UseQueryOptions<AreaResponse | null>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: areaKeys.byId(resourceId),
    queryFn: () => area.byResourceId(resourceId),
    staleTime: 5 * 60 * 1000,
    meta: {
      tags: ['area'],
    },
    ...options,
  });
};

/**
 * Hook for updating the current area
 *
 * Used when user manually changes their location. Automatically invalidates
 * area-related queries to trigger refetches.
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * const updateArea = useUpdateArea();
 *
 * const handleLocationChange = async (newAreaId: string) => {
 *   try {
 *     const newArea = await updateArea.mutateAsync(newAreaId);
 *     // Cache is automatically updated
 *   } catch (error) {
 *     console.error('Failed to update location:', error);
 *   }
 * };
 * ```
 */
export const useUpdateArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newAreaId: string) => {
      return area.byResourceId(newAreaId);
    },
    meta: {
      invalidates: ['area'], // Invalidate all area queries
    },
    onSuccess: (data, newAreaId) => {
      // Update the specific area query cache
      queryClient.setQueryData(areaKeys.byId(newAreaId), data);
    },
  });
};
